// Espace chauffeur — app web mobile (/driver).
//
// Écrans : Connexion (téléphone + code, ou lien d'accès, ou jeton de suivi)
// → Mes courses (Aujourd'hui / À venir / Passées) · Proposées · Profil.
// Le détail et le suivi d'une course restent la page /track/:uid/:token
// existante (même origine, la PWA reste dans son périmètre).
//
// Routes : /chauffeur, /chauffeur/:uid, /chauffeur/:uid/link/:linkId,
// (base /chauffeur : /driver et /driver/:id sont les pages admin « Chauffeurs »)
//          /chauffeur?uid=…&track=… (depuis une page de suivi).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import moment from "moment-timezone";
import "moment/locale/fr";

import { tokens } from "../content/track/tokens";
import { Icon, ICON_PATHS } from "../content/track/icons";
import { bookingProgressStatusList } from "../content/track/progress";
import * as api from "../../../utils/driverApp/api";
import { clearDriverSession, loadDriverSession, saveDriverSession } from "../../../utils/driverApp/session";
import {
  getExistingSubscription,
  isIOS,
  isPushSupported,
  isStandalone,
  permissionState,
  subscribeToPush,
  unsubscribeFromPush,
} from "../../../utils/driverApp/push";

const REFRESH_MS = 60 * 1000;
const ENROUTE_ALERT_MIN = 120;

// ────────────────────────────────────────────────────────────────
// Layout
// ────────────────────────────────────────────────────────────────

const PageBg = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  background: ${tokens.colors.page};
  color: ${tokens.colors.text};
  font-family: ${tokens.typo.family};
  display: flex;
  flex-direction: column;
`;

const Column = styled.div`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Header = styled.div`
  background: linear-gradient(180deg, ${tokens.colors.violetDeep} 0%, ${tokens.colors.violet} 100%);
  color: #fff;
  border-radius: 0 0 ${tokens.radii.lg}px ${tokens.radii.lg}px;
  padding: calc(14px + env(safe-area-inset-top)) 18px 16px;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const HeaderTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.3px;
`;

const HeaderSub = styled.div`
  font-size: 13px;
  opacity: 0.85;
  margin-top: 2px;
`;

const Scroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${tokens.spacings.page}px ${tokens.spacings.page}px calc(84px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacings.gapCards}px;
`;

const Card = styled.div`
  background: ${tokens.colors.surface};
  border-radius: ${tokens.radii.md}px;
  border: 1px solid ${tokens.colors.border};
  box-shadow: ${tokens.shadows.card};
  padding: 12px 14px;
`;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${tokens.colors.muted};
  margin: 6px 2px 0;
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${tokens.colors.muted};
`;

const Empty = styled(Card)`
  text-align: center;
  color: ${tokens.colors.muted};
  padding: 26px 14px;
  font-size: 14px;
`;

const Button = styled.button`
  width: 100%;
  border: none;
  border-radius: ${tokens.radii.sm}px;
  padding: 14px 16px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  background: ${tokens.colors.violet};
  color: #fff;
  box-shadow: ${tokens.shadows.cta};
  &:disabled {
    opacity: 0.5;
    box-shadow: none;
    cursor: default;
  }
  ${(p) =>
    p.$secondary &&
    css`
      background: ${tokens.colors.surface};
      color: ${tokens.colors.violetDeep};
      border: 1px solid ${tokens.colors.border};
      box-shadow: none;
    `}
  ${(p) =>
    p.$danger &&
    css`
      background: ${tokens.colors.dangerTint};
      color: ${tokens.colors.danger};
      border: 1px solid ${tokens.colors.dangerBorder};
      box-shadow: none;
    `}
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${tokens.colors.border};
  border-radius: ${tokens.radii.sm}px;
  padding: 14px;
  font-size: 18px;
  font-family: inherit;
  background: ${tokens.colors.surface};
  color: ${tokens.colors.text};
  letter-spacing: ${(p) => (p.$code ? "6px" : "0")};
  text-align: ${(p) => (p.$code ? "center" : "left")};
  &:focus {
    outline: 2px solid ${tokens.colors.violet};
    border-color: transparent;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${tokens.colors.muted};
  margin: 0 0 6px 2px;
`;

const ErrorBox = styled.div`
  background: ${tokens.colors.errorBg};
  color: ${tokens.colors.errorInk};
  border-radius: ${tokens.radii.sm}px;
  padding: 10px 12px;
  font-size: 14px;
`;

const InfoBox = styled.div`
  background: ${tokens.colors.warnTint};
  color: ${tokens.colors.warnInk};
  border: 1px solid ${tokens.colors.warnBorder};
  border-radius: ${tokens.radii.sm}px;
  padding: 10px 12px;
  font-size: 14px;
`;

const AlertBanner = styled.button`
  width: 100%;
  border: 1px solid ${tokens.colors.dangerBorder};
  background: ${tokens.colors.dangerTint};
  color: ${tokens.colors.danger};
  border-radius: ${tokens.radii.md}px;
  padding: 12px 14px;
  text-align: left;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Segmented = styled.div`
  display: flex;
  background: rgba(255, 255, 255, 0.18);
  border-radius: ${tokens.radii.pill}px;
  padding: 3px;
  margin-top: 14px;
`;

const SegBtn = styled.button`
  flex: 1;
  border: none;
  background: ${(p) => (p.$active ? "#fff" : "transparent")};
  color: ${(p) => (p.$active ? tokens.colors.violetDeep : "#fff")};
  border-radius: ${tokens.radii.pill}px;
  padding: 8px 6px;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
`;

const TabBar = styled.nav`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.97);
  border-top: 1px solid ${tokens.colors.border};
  padding: 6px 0 calc(6px + env(safe-area-inset-bottom));
  display: flex;
  justify-content: center;
  z-index: 5;
`;

const TabBarInner = styled.div`
  width: 100%;
  max-width: 480px;
  display: flex;
`;

const TabBtn = styled.button`
  flex: 1;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  color: ${(p) => (p.$active ? tokens.colors.violetDeep : tokens.colors.muted)};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 4px 0;
  cursor: pointer;
  position: relative;
`;

const Badge = styled.span`
  position: absolute;
  top: 0;
  right: calc(50% - 22px);
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  background: ${tokens.colors.danger};
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
`;

// ── Carte course ──

const RideCard = styled(Card)`
  display: flex;
  gap: 12px;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  opacity: ${(p) => (p.$dimmed ? 0.6 : 1)};
`;

const RideTime = styled.div`
  min-width: 56px;
  text-align: center;
`;

const RideHour = styled.div`
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.5px;
  line-height: 1.1;
`;

const RideDay = styled.div`
  font-size: 11px;
  color: ${tokens.colors.muted};
  text-transform: capitalize;
`;

const RideBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const RideLine = styled.div`
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RideMeta = styled.div`
  font-size: 12px;
  color: ${tokens.colors.muted};
  margin-top: 3px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  border-radius: ${tokens.radii.pill}px;
  padding: 3px 8px;
  background: ${(p) => p.$bg || tokens.colors.surfaceAlt};
  color: ${(p) => p.$ink || tokens.colors.muted};
`;

const Price = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${tokens.colors.violetDeep};
  white-space: nowrap;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid ${tokens.colors.border};
  font-size: 14px;
  &:last-child {
    border-bottom: none;
  }
`;

const InfoKey = styled.span`
  color: ${tokens.colors.muted};
`;

const InfoVal = styled.span`
  font-weight: 600;
  text-align: right;
  word-break: break-word;
`;

// ────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────

function formatPrice(v) {
  if (v === null || v === undefined || v === "" || Number.isNaN(Number(v))) return "";
  return `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
}

function rideMoment(ride) {
  if (ride.pickup_ts) return moment.unix(ride.pickup_ts).tz("Europe/Paris").locale("fr");
  if (ride.date) return moment.unix(ride.date).tz("Europe/Paris").locale("fr");
  return null;
}

function dayLabel(m) {
  if (!m) return "";
  const today = moment().tz("Europe/Paris").startOf("day");
  if (m.isSame(today, "day")) return "Aujourd'hui";
  if (m.isSame(today.clone().add(1, "day"), "day")) return "Demain";
  return m.format("ddd DD MMM");
}

function statusPill(ride) {
  if (ride.cancelled) return { label: "Annulée", bg: tokens.colors.errorBg, ink: tokens.colors.errorInk };
  if (ride.no_show) return { label: "No-show", bg: tokens.colors.warnTint, ink: tokens.colors.warnInk };
  const s = Number(ride.progress_status) || 0;
  if (s >= 4) return { label: "Terminée", bg: "#ECFDF5", ink: tokens.colors.success };
  if (s > 0) return { label: bookingProgressStatusList[s] || "En cours", bg: "#EEF2FF", ink: tokens.colors.violetDeep };
  return { label: "À venir", bg: tokens.colors.surfaceAlt, ink: tokens.colors.muted };
}

function errorMessage(e, fallback) {
  const data = e && e.response && e.response.data;
  if (data && typeof data === "object" && data.error) return data.error;
  if (typeof data === "string" && data.length < 160) return data;
  return fallback;
}

function useDocumentMeta() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Zeplan Chauffeur";
    const head = document.head;
    const links = [];
    const add = (tag, attrs) => {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      head.appendChild(el);
      links.push(el);
    };
    add("link", { rel: "manifest", href: "/driver-manifest.json" });
    add("meta", { name: "apple-mobile-web-app-capable", content: "yes" });
    add("meta", { name: "mobile-web-app-capable", content: "yes" });
    add("meta", { name: "apple-mobile-web-app-title", content: "Chauffeur" });
    add("meta", { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" });
    add("meta", { name: "theme-color", content: tokens.colors.violetDeep });
    add("link", { rel: "apple-touch-icon", href: "/logo192.png" });
    const overlay = document.getElementById("VG_OVERLAY_CONTAINER");
    if (overlay) overlay.style.display = "none";
    return () => {
      document.title = prevTitle;
      links.forEach((el) => el.remove());
      if (overlay) overlay.style.display = "block";
    };
  }, []);
}

// ────────────────────────────────────────────────────────────────
// Connexion
// ────────────────────────────────────────────────────────────────

function LoginScreen({ presetUid, onLoggedIn, bootError }) {
  const [step, setStep] = useState("phone"); // phone | choose | code
  const [phone, setPhone] = useState("");
  const [uid, setUid] = useState(presetUid || "");
  const [tenants, setTenants] = useState([]);
  const [sentInfo, setSentInfo] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(bootError || "");

  const submitPhone = async (e) => {
    if (e) e.preventDefault();
    setError("");
    if (phone.replace(/\D/g, "").length < 8) {
      setError("Entrez votre numéro de téléphone (celui connu de votre opérateur).");
      return;
    }
    setBusy(true);
    try {
      const r = await api.requestCode(uid, phone);
      if (r.sent === "choose") {
        setTenants(r.tenants || []);
        setStep("choose");
      } else if (r.sent === "none") {
        setError(
          "Aucun chauffeur avec ce numéro, ou aucun moyen de vous joindre. Vérifiez le numéro ou demandez votre lien d'accès à votre opérateur."
        );
      } else {
        if (r.uid) setUid(r.uid);
        setSentInfo(r);
        setStep("code");
      }
    } catch (err) {
      setError(errorMessage(err, "Impossible d'envoyer le code. Réessayez."));
    } finally {
      setBusy(false);
    }
  };

  const chooseTenant = async (tenantUid) => {
    setUid(tenantUid);
    setBusy(true);
    setError("");
    try {
      const r = await api.requestCode(tenantUid, phone);
      if (r.sent === "none") setError("Impossible de vous joindre pour cet opérateur.");
      else {
        setSentInfo(r);
        setStep("code");
      }
    } catch (err) {
      setError(errorMessage(err, "Impossible d'envoyer le code. Réessayez."));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const r = await api.verifyCode(uid, phone, code);
      onLoggedIn(r);
    } catch (err) {
      setError(errorMessage(err, "Code incorrect."));
    } finally {
      setBusy(false);
    }
  };

  const channelsText = useMemo(() => {
    if (!sentInfo) return "";
    const parts = [];
    if ((sentInfo.channels || []).includes("whatsapp")) parts.push("WhatsApp");
    if ((sentInfo.channels || []).includes("email")) parts.push(`e-mail${sentInfo.emailHint ? ` (${sentInfo.emailHint})` : ""}`);
    return parts.join(" et ");
  }, [sentInfo]);

  return (
    <PageBg>
      <Column>
        <Header>
          <HeaderTitle>Espace chauffeur</HeaderTitle>
          <HeaderSub>Vos courses, vos bons de mission, le suivi en direct.</HeaderSub>
        </Header>
        <Scroll>
          {step === "phone" && (
            <Card as="form" onSubmit={submitPhone}>
              <Label htmlFor="drv-phone">Votre numéro de téléphone</Label>
              <Input
                id="drv-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="06 12 34 56 78"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoFocus
              />
              <Muted style={{ marginTop: 8 }}>Un code de connexion vous sera envoyé par WhatsApp ou e-mail.</Muted>
              {error && <ErrorBox style={{ marginTop: 10 }}>{error}</ErrorBox>}
              <Button type="submit" disabled={busy} style={{ marginTop: 14 }}>
                {busy ? "Envoi…" : "Recevoir mon code"}
              </Button>
            </Card>
          )}

          {step === "choose" && (
            <Card>
              <Label>Vous travaillez pour plusieurs opérateurs. Lequel ?</Label>
              {tenants.map((t) => (
                <Button key={t.uid} $secondary disabled={busy} onClick={() => chooseTenant(t.uid)} style={{ marginTop: 8 }}>
                  {t.name || t.uid}
                </Button>
              ))}
              {error && <ErrorBox style={{ marginTop: 10 }}>{error}</ErrorBox>}
            </Card>
          )}

          {step === "code" && (
            <Card as="form" onSubmit={submitCode}>
              <Label htmlFor="drv-code">Code reçu{channelsText ? ` par ${channelsText}` : ""}</Label>
              <Input
                id="drv-code"
                $code
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="••••••"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                autoFocus
              />
              <Muted style={{ marginTop: 8 }}>Valable 10 minutes.</Muted>
              {error && <ErrorBox style={{ marginTop: 10 }}>{error}</ErrorBox>}
              <Button type="submit" disabled={busy || code.length !== 6} style={{ marginTop: 14 }}>
                {busy ? "Vérification…" : "Se connecter"}
              </Button>
              <Button
                type="button"
                $secondary
                disabled={busy}
                style={{ marginTop: 8 }}
                onClick={() => {
                  setCode("");
                  setStep("phone");
                }}
              >
                Changer de numéro / renvoyer un code
              </Button>
            </Card>
          )}

          <Muted style={{ textAlign: "center", marginTop: 12 }}>
            Pas de code ? Le lien reçu avec votre bon de mission ouvre aussi cet espace.
          </Muted>
        </Scroll>
      </Column>
    </PageBg>
  );
}

// ────────────────────────────────────────────────────────────────
// Courses
// ────────────────────────────────────────────────────────────────

function RideItem({ ride, onOpen }) {
  const m = rideMoment(ride);
  const pill = statusPill(ride);
  const clickable = Boolean(ride.track_url);
  return (
    <RideCard
      $clickable={clickable}
      $dimmed={ride.cancelled}
      onClick={() => clickable && onOpen(ride)}
      role={clickable ? "button" : undefined}
      data-testid="ride-card"
    >
      <RideTime>
        <RideHour>{ride.time || (m ? m.format("HH:mm") : "—")}</RideHour>
        <RideDay>{dayLabel(m)}</RideDay>
      </RideTime>
      <RideBody>
        <RideLine>{ride.pickup_address || "—"}</RideLine>
        <RideLine style={{ fontWeight: 500, color: tokens.colors.muted }}>→ {ride.dropoff_address || "—"}</RideLine>
        <RideMeta>
          <Pill $bg={pill.bg} $ink={pill.ink}>
            {pill.label}
          </Pill>
          {ride.vehicle && <span>{ride.vehicle}</span>}
          {ride.flight_number && <span>Vol {ride.flight_number}</span>}
          {ride.firstname || ride.lastname ? <span>{`${ride.firstname || ""} ${ride.lastname || ""}`.trim()}</span> : null}
        </RideMeta>
      </RideBody>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "space-between" }}>
        <Price>{formatPrice(ride.driver_price)}</Price>
        {clickable && <Icon path={ICON_PATHS.chevronDown} size={16} sw={2} style={{ transform: "rotate(-90deg)", color: tokens.colors.subtle }} />}
      </div>
    </RideCard>
  );
}

function RidesView({ rides, loading, error, onOpen, onRetry }) {
  const [seg, setSeg] = useState("today");
  const now = moment().tz("Europe/Paris");
  const today = now.clone().startOf("day");

  const groups = useMemo(() => {
    const t = [];
    const up = [];
    const past = [];
    (rides || []).forEach((r) => {
      const m = rideMoment(r);
      if (!m) return;
      if (m.isSame(today, "day")) t.push(r);
      else if (m.isAfter(today)) up.push(r);
      else past.push(r);
    });
    past.reverse();
    return { today: t, upcoming: up, past };
  }, [rides, today]);

  const alertRide = useMemo(() => {
    return (groups.today || []).find((r) => {
      if (r.cancelled || Number(r.progress_status) > 0) return false;
      const m = rideMoment(r);
      if (!m) return false;
      const diff = m.diff(now, "minutes");
      return diff <= ENROUTE_ALERT_MIN && diff > -30 && r.track_url;
    });
  }, [groups.today, now]);

  const list = groups[seg] || [];

  return (
    <>
      <Header>
        <HeaderTop>
          <div>
            <HeaderTitle>Mes courses</HeaderTitle>
            <HeaderSub>{now.locale("fr").format("dddd DD MMMM")}</HeaderSub>
          </div>
        </HeaderTop>
        <Segmented>
          <SegBtn $active={seg === "today"} onClick={() => setSeg("today")}>
            Aujourd'hui{groups.today.length ? ` (${groups.today.length})` : ""}
          </SegBtn>
          <SegBtn $active={seg === "upcoming"} onClick={() => setSeg("upcoming")}>
            À venir{groups.upcoming.length ? ` (${groups.upcoming.length})` : ""}
          </SegBtn>
          <SegBtn $active={seg === "past"} onClick={() => setSeg("past")}>
            Passées
          </SegBtn>
        </Segmented>
      </Header>
      <Scroll>
        {alertRide && (
          <AlertBanner onClick={() => onOpen(alertRide)}>
            <Icon path={ICON_PATHS.alert} size={20} sw={2.2} />
            <span>
              Course à {alertRide.time} — confirmez « Je suis en route » dans le suivi.
            </span>
          </AlertBanner>
        )}
        {error && (
          <ErrorBox>
            {error}{" "}
            <button type="button" onClick={onRetry} style={{ background: "none", border: "none", color: "inherit", textDecoration: "underline", fontFamily: "inherit" }}>
              Réessayer
            </button>
          </ErrorBox>
        )}
        {loading && !rides ? (
          <Empty>Chargement…</Empty>
        ) : list.length === 0 ? (
          <Empty>
            {seg === "today" && "Aucune course aujourd'hui."}
            {seg === "upcoming" && "Aucune course à venir."}
            {seg === "past" && "Aucune course sur les 30 derniers jours."}
          </Empty>
        ) : (
          list.map((r) => <RideItem key={r.id} ride={r} onOpen={onOpen} />)
        )}
        {seg !== "past" && list.length > 0 && (
          <Muted style={{ textAlign: "center" }}>Touchez une course pour ouvrir le bon de mission et le suivi.</Muted>
        )}
      </Scroll>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// Courses proposées
// ────────────────────────────────────────────────────────────────

function OfferItem({ offer, onAccept, onDecline, busy }) {
  const [confirm, setConfirm] = useState(false);
  const m = offer.pickup_ts ? moment.unix(offer.pickup_ts).tz("Europe/Paris").locale("fr") : null;
  return (
    <Card data-testid="offer-card">
      <Row>
        <div>
          <RideHour>{m ? m.format("HH:mm") : offer.time || "—"}</RideHour>
          <RideDay>{m ? `${dayLabel(m)} · ${m.format("DD MMM")}` : ""}</RideDay>
        </div>
        <Price style={{ fontSize: 20 }}>{formatPrice(offer.driver_price) || "Prix à confirmer"}</Price>
      </Row>
      <div style={{ marginTop: 10 }}>
        <RideLine style={{ whiteSpace: "normal" }}>{offer.pickup_address}</RideLine>
        {offer.step_address && <RideLine style={{ whiteSpace: "normal", fontWeight: 500, color: tokens.colors.muted }}>Arrêt : {offer.step_address}</RideLine>}
        <RideLine style={{ whiteSpace: "normal", fontWeight: 500, color: tokens.colors.muted }}>→ {offer.dropoff_address}</RideLine>
      </div>
      <RideMeta>
        {offer.vehicle && <Pill>{offer.vehicle}</Pill>}
        {offer.passengers ? <span>{offer.passengers} pax</span> : null}
        {offer.bags ? <span>{offer.bags} bag.</span> : null}
        {offer.flight_number && <span>Vol {offer.flight_number}</span>}
      </RideMeta>
      {!confirm ? (
        <Actions>
          <Button $secondary disabled={busy} onClick={() => onDecline(offer)} style={{ flex: 1 }}>
            Pas dispo
          </Button>
          <Button disabled={busy} onClick={() => setConfirm(true)} style={{ flex: 2 }}>
            Accepter
          </Button>
        </Actions>
      ) : (
        <Actions>
          <Button $secondary disabled={busy} onClick={() => setConfirm(false)} style={{ flex: 1 }}>
            Annuler
          </Button>
          <Button disabled={busy} onClick={() => onAccept(offer)} style={{ flex: 2 }}>
            {busy ? "…" : "Je confirme, je prends la course"}
          </Button>
        </Actions>
      )}
    </Card>
  );
}

function OffersView({ offers, loading, error, onAccept, onDecline, busyId, notice }) {
  return (
    <>
      <Header>
        <HeaderTitle>Courses proposées</HeaderTitle>
        <HeaderSub>Premier à accepter, premier servi.</HeaderSub>
      </Header>
      <Scroll>
        {notice && <InfoBox>{notice}</InfoBox>}
        {error && <ErrorBox>{error}</ErrorBox>}
        {loading && !offers ? (
          <Empty>Chargement…</Empty>
        ) : !offers || offers.length === 0 ? (
          <Empty>Aucune course proposée pour le moment. Vous serez notifié dès qu'une course est disponible.</Empty>
        ) : (
          offers.map((o) => (
            <OfferItem key={o.bookingId} offer={o} onAccept={onAccept} onDecline={onDecline} busy={busyId === o.bookingId} />
          ))
        )}
      </Scroll>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// Profil
// ────────────────────────────────────────────────────────────────

function ProfileView({ me, session, onLogout }) {
  const driver = (me && me.driver) || session.driver || {};
  const tenantName = (me && me.tenant && me.tenant.name) || session.tenantName || "";
  const [pushState, setPushState] = useState("idle"); // idle | on | off | busy | unsupported | denied
  const [pushError, setPushError] = useState("");
  const standalone = isStandalone();
  const ios = isIOS();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) {
        setPushState("unsupported");
        return;
      }
      if (permissionState() === "denied") {
        setPushState("denied");
        return;
      }
      const sub = await getExistingSubscription().catch(() => null);
      if (!cancelled) setPushState(sub ? "on" : "off");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const enablePush = async () => {
    setPushError("");
    setPushState("busy");
    try {
      const sub = await subscribeToPush(me && me.push && me.push.publicKey);
      await api.pushSubscribe(session, sub.toJSON());
      setPushState("on");
    } catch (e) {
      setPushError(e && e.message ? e.message : "Activation impossible");
      setPushState(permissionState() === "denied" ? "denied" : "off");
    }
  };

  const disablePush = async () => {
    setPushState("busy");
    try {
      const endpoint = await unsubscribeFromPush();
      if (endpoint) await api.pushUnsubscribe(session, endpoint);
    } catch (e) {
      // ignore
    }
    setPushState("off");
  };

  return (
    <>
      <Header>
        <HeaderTitle>{driver.name || "Chauffeur"}</HeaderTitle>
        <HeaderSub>{[driver.company && driver.company.name, tenantName].filter(Boolean).join(" · ")}</HeaderSub>
      </Header>
      <Scroll>
        <SectionLabel>Notifications</SectionLabel>
        <Card>
          {pushState === "unsupported" && (
            <Muted>
              {ios && !standalone
                ? "Sur iPhone, ajoutez d'abord l'app à l'écran d'accueil (Partager → Sur l'écran d'accueil), puis revenez ici pour activer les notifications."
                : "Les notifications ne sont pas prises en charge par ce navigateur."}
            </Muted>
          )}
          {pushState === "denied" && (
            <Muted>Notifications bloquées dans les réglages du navigateur. Autorisez-les puis rechargez cette page.</Muted>
          )}
          {(pushState === "off" || pushState === "busy") && (
            <>
              <Muted>Recevez vos bons de mission, les courses proposées et les rappels directement sur ce téléphone.</Muted>
              {!(me && me.push && me.push.enabled) && (
                <InfoBox style={{ marginTop: 10 }}>Les notifications ne sont pas encore activées par votre opérateur.</InfoBox>
              )}
              <Button
                onClick={enablePush}
                disabled={pushState === "busy" || !(me && me.push && me.push.enabled)}
                style={{ marginTop: 12 }}
              >
                {pushState === "busy" ? "Activation…" : "Activer les notifications"}
              </Button>
            </>
          )}
          {pushState === "on" && (
            <>
              <Row>
                <span style={{ fontWeight: 600 }}>Notifications activées sur cet appareil</span>
                <Pill $bg="#ECFDF5" $ink={tokens.colors.success}>✓</Pill>
              </Row>
              <Button $secondary onClick={disablePush} style={{ marginTop: 12 }}>
                Désactiver sur cet appareil
              </Button>
            </>
          )}
          {pushError && <ErrorBox style={{ marginTop: 10 }}>{pushError}</ErrorBox>}
        </Card>

        {!standalone && (
          <>
            <SectionLabel>Installer l'app</SectionLabel>
            <Card>
              <Muted>
                {ios
                  ? "Safari : bouton Partager → « Sur l'écran d'accueil ». L'app s'ouvre ensuite comme une application."
                  : "Menu du navigateur → « Ajouter à l'écran d'accueil » (ou « Installer l'application »)."}
              </Muted>
            </Card>
          </>
        )}

        <SectionLabel>Mes informations</SectionLabel>
        <Card>
          <InfoRow>
            <InfoKey>Nom</InfoKey>
            <InfoVal>{driver.name || "—"}</InfoVal>
          </InfoRow>
          <InfoRow>
            <InfoKey>Téléphone</InfoKey>
            <InfoVal>{driver.phone || "—"}</InfoVal>
          </InfoRow>
          <InfoRow>
            <InfoKey>Société</InfoKey>
            <InfoVal>{(driver.company && driver.company.name) || "—"}</InfoVal>
          </InfoRow>
          <InfoRow>
            <InfoKey>Véhicule</InfoKey>
            <InfoVal>{[driver.vehicleType, driver.plate].filter(Boolean).join(" · ") || "—"}</InfoVal>
          </InfoRow>
          <InfoRow>
            <InfoKey>Opérateur</InfoKey>
            <InfoVal>{tenantName || "—"}</InfoVal>
          </InfoRow>
        </Card>
        <Muted style={{ margin: "4px 2px" }}>Une information est fausse ? Contactez votre opérateur, il met votre fiche à jour.</Muted>

        <Button $danger onClick={onLogout} style={{ marginTop: 8 }}>
          Se déconnecter
        </Button>
      </Scroll>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// Écran principal
// ────────────────────────────────────────────────────────────────

export default function DriverApp() {
  const params = useParams();
  const location = useLocation();
  const history = useHistory();
  useDocumentMeta();

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const presetUid = params.uid || query.get("uid") || "";
  const linkId = params.linkId || "";
  const trackToken = query.get("track") || "";

  const [session, setSession] = useState(() => loadDriverSession());
  const [booting, setBooting] = useState(true);
  const [bootError, setBootError] = useState("");
  const [tab, setTab] = useState(location.hash === "#offers" ? "offers" : "rides");
  const [me, setMe] = useState(null);
  const [rides, setRides] = useState(null);
  const [ridesError, setRidesError] = useState("");
  const [offers, setOffers] = useState(null);
  const [offersError, setOffersError] = useState("");
  const [offersNotice, setOffersNotice] = useState("");
  const [busyId, setBusyId] = useState("");
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const applyLogin = useCallback(
    (r) => {
      const next = { uid: r.uid, session: r.session, driver: r.driver || null, tenantName: "" };
      saveDriverSession(next);
      setSession(next);
      setBootError("");
      if (params.uid || params.linkId || location.search) history.replace("/chauffeur" + (location.hash || ""));
    },
    [history, location.hash, location.search, params.linkId, params.uid]
  );

  const signOut = useCallback(async () => {
    if (session) await api.logout(session);
    clearDriverSession();
    setSession(null);
    setMe(null);
    setRides(null);
    setOffers(null);
    setTab("rides");
  }, [session]);

  // Démarrage : lien d'accès, jeton de suivi, ou session existante.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (linkId && presetUid) {
          const r = await api.loginWithLink(presetUid, linkId);
          if (!cancelled) applyLogin(r);
          return;
        }
        if (trackToken && presetUid) {
          const stored = loadDriverSession();
          if (!stored || stored.uid !== presetUid) {
            const r = await api.loginFromTrack(presetUid, trackToken);
            if (!cancelled) applyLogin(r);
          } else {
            history.replace("/chauffeur");
          }
          return;
        }
      } catch (e) {
        if (!cancelled) {
          clearDriverSession();
          setSession(null);
          setBootError(errorMessage(e, "Lien invalide ou expiré. Connectez-vous avec votre numéro."));
        }
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(
    async (silent = false) => {
      if (!session) return;
      if (!silent) setLoading(true);
      const [meRes, ridesRes, offersRes] = await Promise.allSettled([api.getMe(session), api.getRides(session), api.getOffers(session)]);
      if (meRes.status === "fulfilled") {
        setMe(meRes.value);
        const tenantName = (meRes.value.tenant && meRes.value.tenant.name) || "";
        if (tenantName !== session.tenantName || JSON.stringify(meRes.value.driver) !== JSON.stringify(session.driver)) {
          const next = { ...session, driver: meRes.value.driver, tenantName };
          saveDriverSession(next);
        }
      } else {
        const status = meRes.reason && meRes.reason.response && meRes.reason.response.status;
        if (status === 401 || status === 403) {
          clearDriverSession();
          setSession(null);
          setBootError("Votre session a expiré. Reconnectez-vous.");
          setLoading(false);
          return;
        }
      }
      if (ridesRes.status === "fulfilled") {
        setRides(ridesRes.value.rides || []);
        setRidesError("");
      } else setRidesError(errorMessage(ridesRes.reason, "Impossible de charger vos courses."));
      if (offersRes.status === "fulfilled") {
        setOffers(offersRes.value.offers || []);
        setOffersError("");
      } else setOffersError(errorMessage(offersRes.reason, "Impossible de charger les courses proposées."));
      setLoading(false);
    },
    [session]
  );

  useEffect(() => {
    if (!session || booting) return undefined;
    refresh();
    timer.current = setInterval(() => refresh(true), REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer.current);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [session, booting, refresh]);

  const openRide = useCallback(
    (ride) => {
      if (ride && ride.track_url) history.push(ride.track_url);
    },
    [history]
  );

  const accept = useCallback(
    async (offer) => {
      setBusyId(offer.bookingId);
      setOffersNotice("");
      try {
        const r = await api.acceptOffer(session, offer.bookingId);
        if (r && r.track_url) {
          history.push(r.track_url);
          return;
        }
        setOffersNotice("Course acceptée. Elle apparaît dans « Mes courses ».");
        await refresh(true);
        setTab("rides");
      } catch (e) {
        const status = e && e.response && e.response.status;
        if (status === 409) setOffersNotice("Trop tard : cette course vient d'être prise par un autre chauffeur.");
        else if (status === 410) setOffersNotice("Cette course n'est plus proposée.");
        else setOffersNotice(errorMessage(e, "Impossible d'accepter la course. Réessayez."));
        await refresh(true);
      } finally {
        setBusyId("");
      }
    },
    [history, refresh, session]
  );

  const decline = useCallback(
    async (offer) => {
      setOffers((prev) => (prev || []).filter((o) => o.bookingId !== offer.bookingId));
      try {
        await api.declineOffer(session, offer.bookingId);
      } catch (e) {
        // l'offre réapparaîtra au prochain rafraîchissement si l'appel a échoué
      }
    },
    [session]
  );

  if (booting) {
    return (
      <PageBg>
        <Column>
          <Header>
            <HeaderTitle>Espace chauffeur</HeaderTitle>
            <HeaderSub>Connexion…</HeaderSub>
          </Header>
        </Column>
      </PageBg>
    );
  }

  if (!session) {
    return <LoginScreen presetUid={presetUid} onLoggedIn={applyLogin} bootError={bootError} />;
  }

  const offersCount = (offers || []).length;

  return (
    <PageBg>
      <Column>
        {tab === "rides" && <RidesView rides={rides} loading={loading} error={ridesError} onOpen={openRide} onRetry={() => refresh()} />}
        {tab === "offers" && (
          <OffersView
            offers={offers}
            loading={loading}
            error={offersError}
            onAccept={accept}
            onDecline={decline}
            busyId={busyId}
            notice={offersNotice}
          />
        )}
        {tab === "profile" && <ProfileView me={me} session={session} onLogout={signOut} />}
      </Column>
      <TabBar>
        <TabBarInner>
          <TabBtn $active={tab === "rides"} onClick={() => setTab("rides")}>
            <Icon path={ICON_PATHS.car} size={20} sw={2} />
            Mes courses
          </TabBtn>
          <TabBtn $active={tab === "offers"} onClick={() => setTab("offers")} data-testid="tab-offers">
            <Icon path={ICON_PATHS.clock} size={20} sw={2} />
            Proposées
            {offersCount > 0 && <Badge>{offersCount}</Badge>}
          </TabBtn>
          <TabBtn $active={tab === "profile"} onClick={() => setTab("profile")}>
            <Icon path={ICON_PATHS.note} size={20} sw={2} />
            Profil
          </TabBtn>
        </TabBarInner>
      </TabBar>
    </PageBg>
  );
}
